import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';

const createQueryParams = (page, pageSize, filters) => {
  const params = new URLSearchParams();

  if (page) params.append('page', page);
  if (pageSize) params.append('page_size', pageSize);
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params.append(key, filters[key]);
    });
  }

  return params.toString();
};

export const fetchOxyzoCurrentDues = createAsyncThunk(
  'currentDues/fetchOxyzoCurrentDues',
  async ({ jwtBearer, page, pageSize, filters } = {}, { rejectWithValue }) => {
    try {
      const queryParams = createQueryParams(page, pageSize, filters);
      const response = await axios.get(
        `${BASE_URL}oxyzo/current/dues/data/${queryParams ? `?${queryParams}` : ''}`,
        {
          headers: {
            Authorization: `Bearer ${jwtBearer}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.reload();
      }
      console.error('Fetch error:', error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

export const fetchFilteredOxyzoCurrentDues = createAsyncThunk(
  'currentDues/fetchFilteredOxyzoCurrentDues',
  async ({ jwtBearer, filters } = {}) => {
    const queryParams = createQueryParams(null, null, filters);
    const response = await axios.get(
      `${BASE_URL}oxyzo/current/dues/data/${queryParams ? `?${queryParams}` : ''}`,
      {
        headers: {
          Authorization: `Bearer ${jwtBearer}`,
        },
      }
    );
    return response.data;
  }
);

export const downloadOxyzoCurrentDuesReport = createAsyncThunk(
  'currentDues/downloadOxyzoCurrentDuesReport',
  async ({ jwtBearer } = {}, { rejectWithValue }) => {
    try {
      const fileName = 'Current_Dues_Oxyzo.csv';

      const response = await fetch(`${BASE_URL}oxyzo/report/download/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwtBearer}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'current_dues',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading current dues report:', error);
      return rejectWithValue(error.message);
    }
  }
);

const oxyzoCurrentDuesSlice = createSlice({
  name: 'oxyzoCurrentDues',
  initialState: {
    data: [],
    loading: false,
    error: null,
    totalCount: 0,

    filteredData: [],
    filteredLoading: false,
    filteredError: null,
    filteredTotalCount: 0,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOxyzoCurrentDues.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOxyzoCurrentDues.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data.current_dues_data;
        state.totalCount = action.payload.data.total_current_dues_data;
      })
      .addCase(fetchOxyzoCurrentDues.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchFilteredOxyzoCurrentDues.pending, (state) => {
        state.filteredLoading = true;
        state.filteredError = null;
      })
      .addCase(fetchFilteredOxyzoCurrentDues.fulfilled, (state, action) => {
        state.filteredLoading = false;
        state.filteredData = action.payload.data.current_dues_data;
        state.filteredTotalCount = action.payload.data.total_current_dues_data || 0;
      })
      .addCase(fetchFilteredOxyzoCurrentDues.rejected, (state, action) => {
        state.filteredLoading = false;
        state.filteredError = action.error.message;
      });
  },
});

export default oxyzoCurrentDuesSlice.reducer;
